import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import AiGeneratedBadge from 'components/AiGeneratedBadge'
import ConfirmModal from 'components/ConfirmModal'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import type { RegenerateErrorKind } from './regenerateError'
import { formatRewardValue } from './rewardValue'

const t = buildTranslate('recovery_ai.settings.approved_rewards.ladder')

// Rows shown before the "View N more" expander collapses the rest. The
// auto-provisioned make-good set can be ~8 tiers (wave-6 decision), so the
// ladder collapses like the picker's selected-chip list.
const INITIAL_VISIBLE_ROWS = 3

// overrides.BaseButton REPLACES uikit's minimal styling entirely, so every
// state must be restated — :hover:enabled needs that exact :enabled qualifier
// to outrank uikit's own same-specificity default, else it renders black.
// belizeHole10 matches the hover tint used elsewhere for minimal-kind links
// (ButtonPanel.tsx, CustomDateRangeFilter.tsx). Mirrored in RewardMultiSelect.
const TEXT_LINK_BUTTON_OVERRIDES = {
  BaseButton: {
    style: {
      fontWeight: 500,
      color: merchantTheme.colors.belizeHole50,
      backgroundColor: 'transparent',
      paddingLeft: '0',
      paddingRight: '0',
      paddingTop: '0',
      paddingBottom: '0',
      ':hover:enabled': { backgroundColor: merchantTheme.colors.belizeHole10 },
      ':active:enabled': { backgroundColor: merchantTheme.colors.belizeHole10 },
    },
  },
}

// Normalized ladder row, mapped from the make-good curation snapshot
// (`MakeGoodOffer`) by the caller (ApprovedRewardsBody). Kept as its own shape
// so this component stays purely presentational across its curation surfaces
// (Settings pane, config modal, onboarding wizard step).
export type LadderRow = {
  id: string
  label: string
  // The guest-facing "up to $X off" VALUE cap, in integer cents (CONTRACT
  // §5.8) — nexus `Recovery::MakeGoodOffer#up_to_cents`. This is NOT cost:
  // nexus's own `assumed_cost_cents` (up_to/2, a derived assumption fed to
  // the AI brain) is deliberately never displayed anywhere on this screen.
  // The old name `costCents` was a naming collision that made this number
  // look comparable to the picker's COGS cell, when it was guest value all
  // along (see nexus build_consumer_context.rb:337: "value_cents is the full
  // up_to_cents ... IS the value cap the guest sees").
  valueCents: number
  // A declined row (merchant ×'d it, materialized or not) is hidden from the
  // ladder. A materialized row is a LIVE, already-granted program — disabling
  // it is a real write (nexus #1640), not cosmetic.
  //
  // context: nexus #1640 excludes declined_at from the BE's `offerable` scope,
  // so a disabled materialized row drops out of the AI's reward menu exactly
  // like declining a proffered one. Regenerate is the sole way back (no
  // per-row restore wired here, by design) and only re-enables it while the
  // tier is still in the current ladder.
  declined?: boolean
  materialized?: boolean
}

type Props = {
  /**
   * The normalized ladder rows to render. The caller owns the source split +
   * mapping; this component is presentational.
   */
  rows: LadderRow[]
  /**
   * When provided, each row renders a "×" that calls onDecline(id) — the ladder
   * curation control (AI-330), shared by the Settings pane, the config modal,
   * and the onboarding wizard step (AI-335). Omitted ⇒ the ladder is read-only.
   * The decline mutation + fail-soft handling live in the caller; this
   * component only invokes the callback.
   */
  onDecline?: (id: string) => void
  /**
   * Disables every row's "×" while a decline mutation is in flight, so a
   * double-click (or clicking a second row before the first resolves) can't
   * fire duplicate writes — mirrors isRegenerating below. The host's decline
   * mutation has no per-row identity, so this is section-wide, not per-row.
   */
  isDeclining?: boolean
  /**
   * When provided, a section-level "Regenerate rewards" button renders at the
   * BOTTOM of the ladder (AI-331) — never per row. It stays available while
   * curation is on regardless of how many rows the merchant has declined, so
   * declining every reward is never a dead end: clicking restores the full
   * recommended set (declined tiers return, newly-added tiers surface). The
   * regenerate mutation + fail-soft handling live in the caller; this component
   * only invokes the callback. Omitted ⇒ no button (read-only ladder).
   */
  onRegenerate?: () => void
  /**
   * Disables the Regenerate button and shows its spinner while the regenerate
   * mutation is in flight, so a double-click can't fire duplicate writes.
   */
  isRegenerating?: boolean
  /**
   * Host confirms the snapshot fetch SUCCEEDED (`isSuccess`), so empty `rows`
   * means "200 with zero offers", not "BE unreachable". Defaults false to
   * preserve the CONTRACT §3 degradation. With `onRegenerate`, unlocks the empty
   * state — the only recovery path, since an empty snapshot otherwise hides the
   * very button that fixes it.
   */
  isLoaded?: boolean
  /**
   * Kind of the last generate/regenerate failure, or undefined when the write
   * has not failed. 'conflict' (409) means the regenerate would change nothing
   * (the presented set already matches the recommended one) — not terminal, so
   * the button stays enabled for a decline-then-retry; 'error' keeps the
   * cause-neutral retry copy. i18n owned here: the host derives the kind, never
   * passes the raw error.
   */
  regenerateError?: RegenerateErrorKind
  /**
   * Whether the last decline/disable write failed. Cause-neutral — decline has
   * no non-terminal status to special-case (unlike regenerate's 409) — and
   * surfaced the same way: inline, beside the control that caused it. The
   * host derives this from the mutation state; this component never sees the
   * raw error.
   */
  declineError?: boolean
  /**
   * Read-only mode (AI-64 CASL gate): the host has `access` but not `manage` on
   * RecoveryAI. Both writes this section owns — the per-row decline "×" and the
   * section-level Regenerate — go disabled; the ladder itself still renders and
   * the "View N more" expander stays live (reading is not a write). Defaults
   * false so every existing call site is unchanged.
   */
  disabled?: boolean
}

// "Make-good ladder": lists the make-good reward offers Thanx AI proffers for
// the merchant (AI-172/AI-247/AI-330/AI-1121), each badged "AI recommended".
// When `onDecline` is provided EVERY row — materialized (a live, already-
// granted program) included — shows a "×" that disables/declines it (an
// immediate write; a persistent helper says so); without `onDecline` the
// ladder is read-only. Materialized rows render like any other row (no
// separate pill) — but disabling one is a live write, not just curation
// (nexus #1640 pulls it from the AI's reward menu). Declined rows hide
// immediately; Regenerate is the sole way back (no per-row restore, by
// design) and only while the tier is still in the current ladder. When
// `onRegenerate` is provided a section-level button renders at the bottom
// (AI-331), staying available even after every reward is declined so the
// ladder is never a dead end. An empty snapshot renders null — degrading to
// exactly the picker-only layout — UNLESS the host passes `isLoaded`, i.e.
// confirms the fetch succeeded and the emptiness is real rather than an
// unreachable backend.
const MakeGoodLadder = ({
  rows,
  onDecline,
  isDeclining = false,
  onRegenerate,
  isRegenerating = false,
  isLoaded = false,
  regenerateError,
  declineError = false,
  disabled = false,
}: Props): React.ReactElement | null => {
  const [css] = useStyletron()
  const [expanded, setExpanded] = useState(false)
  // Only armed on the rows-present path — the empty snapshot has no curation to
  // destroy, so it skips the dialog (see the button below).
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false)

  const isEmptySnapshot = rows.length === 0
  // NB: says nothing about emptiness — it's the host's write + a CONFIRMED fetch.
  // Pair it with isEmptySnapshot; alone it's true on the rows-present path too.
  const canGenerate = Boolean(onRegenerate) && isLoaded

  // Degrade to picker-only when the snapshot is empty with no way out of it
  // (CONTRACT §3) — an unconfirmed [] may just be an unreachable BE. Keyed off
  // the RAW row count so "every row declined" is handled below instead.
  if (isEmptySnapshot && !canGenerate) {
    return null
  }

  // Hide only declined rows before the slice/expander math so the "View N more"
  // count operates on exactly what renders. Materialized rows stay visible —
  // a fully-enabled merchant has every tier materialized, and hiding them
  // left the ladder blank.
  const visibleRows = rows.filter(row => !row.declined)

  // Every row declined. Without a regenerate affordance there is nothing
  // actionable to show, so preserve the read-only collapse; with regenerate
  // (curation on) keep the section so the merchant can restore.
  if (visibleRows.length === 0 && !onRegenerate) {
    return null
  }

  // Every visible row (materialized included, AI-1121) carries a "×", but its
  // disabled-ness (read-only, or a decline in flight — see isDeclining above)
  // means the "×" isn't always usable. Gate the helper the same way, or a
  // view-only operator sees "saves automatically" next to inert controls.
  const hasDeclinableRow =
    Boolean(onDecline) && !disabled && visibleRows.length > 0
  const declineDisabled = disabled || isDeclining

  // Two empty-state copies: a snapshot never generated vs rows that all declined.
  const emptyStateCopy = isEmptySnapshot ? t('none_yet') : t('all_declined')

  const hiddenCount = visibleRows.length - INITIAL_VISIBLE_ROWS
  const shownRows = expanded
    ? visibleRows
    : visibleRows.slice(0, INITIAL_VISIBLE_ROWS)

  return (
    <div className="d-flex flex-column mb-xl">
      {/* Figma parity: the section reads as a small bold label (BodyText3 bold,
          16px) over a 14px subtitle, not the former 18px Header4 heading. */}
      <Text.BodyText3 bold>{t('title')}</Text.BodyText3>
      <Text.BodyText4 color="grey70" className="mt-xs">
        {t('subtitle')}
      </Text.BodyText4>

      {visibleRows.length > 0 ? (
        <>
          {/* Row list mirrors the RewardMultiSelect selected-chip idiom: each row
              is its own bordered, rounded card separated by an 8px gap (Figma
              parity — replaces the former single container with hairline dividers),
              and (when onDecline is provided) the same icon-only "×" remove
              affordance. */}
          <div className={`mt-s d-flex flex-column ${css({ gap: '8px' })}`}>
            {shownRows.map(row => (
              <div
                key={row.id}
                className={`d-flex align-items-center justify-content-between ${css(
                  {
                    padding: '12px 16px',
                    border: `1px solid ${merchantTheme.colors.grey20}`,
                    borderRadius: '6px',
                  }
                )}`}
              >
                <div
                  className={`d-flex align-items-center ${css({
                    gap: '12px',
                  })}`}
                >
                  <AiGeneratedBadge />
                  <Text.BodyText4>{row.label}</Text.BodyText4>
                </div>
                <div
                  className={`d-flex align-items-center ${css({
                    gap: '12px',
                  })}`}
                >
                  {/* grey70 (#6A747F, ~4.6:1) clears WCAG 1.4.3; matches the
                      picker's guest-value cell (same shared copy, via
                      formatRewardValue). */}
                  <Text.BodyText4 color="grey70">
                    {formatRewardValue(row.valueCents)}
                  </Text.BodyText4>
                  {onDecline && (
                    // Icon-only "×": a native <button> is a documented WCAG 2.5.8
                    // target-size exemption (see uikit Button overrides REPLACE),
                    // so we mirror the RewardMultiSelect chip-remove idiom rather
                    // than a uikit Button. aria-label carries the name via i18n,
                    // distinguishing "disable" (materialized — a live write per
                    // nexus #1640, see LadderRow.materialized) from "remove"
                    // (not-yet-live, never was). Figma-parity color: grey40,
                    // lighter than the WCAG-passing grey70 used elsewhere — an
                    // accepted trade-off to match the design exactly.
                    <button
                      type="button"
                      aria-label={t(
                        row.materialized
                          ? 'disable_reward_aria_label'
                          : 'remove_reward_aria_label',
                        { name: row.label }
                      )}
                      disabled={declineDisabled}
                      onClick={() => onDecline(row.id)}
                      className={css({
                        background: 'none',
                        border: 'none',
                        padding: '4px 8px',
                        cursor: declineDisabled ? 'not-allowed' : 'pointer',
                        color: merchantTheme.colors.grey40,
                        lineHeight: 1,
                      })}
                    >
                      <i className="fa fa-times" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Immediate-write UX (CONTRACT §5 resolved): a decline/disable persists
              on click, so a persistent helper sets that expectation. Only shown
              when there's a visible row to act on — read-only mode has no "×"
              anywhere, so there is nothing to save. */}
          {hasDeclinableRow && (
            <Text.BodyText4 color="grey70" className="mt-xs">
              {t('saves_automatically')}
            </Text.BodyText4>
          )}

          {hiddenCount > 0 && (
            <div className="mt-s">
              {/* uikit Button kind="minimal" forwards aria-expanded to the real
                  <button> (borderless text-link toggle, per CLAUDE.md); the
                  overrides restate it as a non-bold flush link (see const above). */}
              <Button
                kind="minimal"
                onClick={() => setExpanded(prev => !prev)}
                aria-expanded={expanded}
                overrides={TEXT_LINK_BUTTON_OVERRIDES}
              >
                {expanded
                  ? t('show_less')
                  : t(hiddenCount === 1 ? 'view_more_one' : 'view_more_other', {
                      count: hiddenCount,
                    })}
              </Button>
            </div>
          )}
        </>
      ) : (
        // Never the BE-absent degradation (that returned null above). The
        // always-present button below is the way out of every case here.
        <Text.BodyText3 color="grey70" className="mt-s">
          {emptyStateCopy}
        </Text.BodyText3>
      )}

      {/* Write failed — surfaced INSIDE this section (never a page-level gate,
          CONTRACT §3), beside the control that caused it. A 409 means nothing
          changed (not terminal); everything else stays retryable too. */}
      {regenerateError && (
        <div className="mt-m">
          <Notification kind="negative" size="full">
            {t(
              regenerateError === 'conflict' ? 'no_changes' : 'regenerate_error'
            )}
          </Notification>
        </div>
      )}

      {/* Decline/disable failed (AI-1121 follow-up) — same treatment as
          regenerate above, just cause-neutral only (no conflict variant). */}
      {declineError && (
        <div className="mt-m">
          <Notification kind="negative" size="full">
            {t('decline_error')}
          </Notification>
        </div>
      )}

      {onRegenerate && (
        <>
          <div className="mt-m">
            {/* Section-level control (AI-332), never per row, so declining every
                reward is never a dead end. Rows present ⇒ the write destroys
                curation (clears declines, no undo) ⇒ confirm dialog. Empty
                snapshot ⇒ nothing to destroy ⇒ fires directly, reads "Generate".
                Disabled only in flight (double-click guard) — a 409 is not
                terminal, so a decline-then-retry must stay clickable (AI-858
                follow-up; previously stuck disabled after any 409). */}
            <Button
              kind="secondary"
              onClick={
                isEmptySnapshot
                  ? onRegenerate
                  : () => setConfirmingRegenerate(true)
              }
              disabled={disabled || isRegenerating}
              isLoading={isRegenerating}
            >
              {isEmptySnapshot ? t('generate') : t('regenerate')}
            </Button>
          </div>
          {!isEmptySnapshot && (
            <ConfirmModal
              isOpen={confirmingRegenerate}
              title={t('regenerate_confirm_title')}
              description={t('regenerate_confirm_body')}
              confirmText={t('regenerate_confirm_cta')}
              isLoading={isRegenerating}
              onClose={() => setConfirmingRegenerate(false)}
              onConfirm={() => {
                onRegenerate?.()
                setConfirmingRegenerate(false)
              }}
              isJustified
            />
          )}
        </>
      )}
    </div>
  )
}

export default MakeGoodLadder
